import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Trans } from 'services/i18n';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { MakeCopy } from 'ui/icons/action/MakeCopy';
import { EmptyStateImages } from 'ui/illustrations/EmptyStateImages';
import { NoResult } from 'ui/illustrations/NoResult';
import { Spinner } from 'ui/spinner';
import { Caption, colors, duration, easing, fontSizes, fontWeights, spacing } from 'ui/theme';

import { usePanelContext } from '../contexts/Panel';
import { useFilterContext } from '../contexts/FilterContext';
import { HiddenFileInput } from './HiddenFileInput';
import { useTranslations } from '../hooks/useTranslations';
import { PanelView } from '../types';

const CaptionSecondary = styled(Caption)`
  color: inherit;
  text-align: center;
`;

const CtaHeader = styled.div`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s20};
  font-weight: ${fontWeights.semiBold};
`;

const Cta = styled.div`
  font-size: ${fontSizes.s16};
`;

const EmptyState = styled.div<{ $isClickable: boolean; $isDragging: boolean; $isLoading: boolean }>`
  align-items: stretch;
  border-color: ${colors.grey15};
  border-radius: ${spacing.quarter};
  border-style: ${({ $isClickable }) => ($isClickable ? 'dashed' : 'solid')};
  border-width: ${spacing.single};
  bottom: 0;
  color: ${colors.textSecondary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 0;
  padding: ${spacing.plusHalf};
  position: absolute;
  right: 0;
  row-gap: ${spacing.base};
  top: 0;
  transition: all ${duration.short} ${easing.entrance};

  ${({ $isClickable, $isDragging, $isLoading }) =>
    $isClickable && !$isLoading
      ? `
      &:hover {
        background-color: ${colors.purple10};
        border-color: ${colors.purple50};
        border-style: solid;
        color: ${colors.purple90};
        cursor: ${$isDragging ? 'auto' : 'pointer'};
      }
      `
      : ''};
  ${({ $isDragging, $isLoading }) =>
    $isDragging && !$isLoading
      ? `
      background-color: ${colors.purple10};
      border-color: ${colors.purple50};
      border-style: solid;
      color: ${colors.purple90}
      `
      : ''};
`;

const StyledLabel = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: ${fontSizes.s14};
  font-weight: ${fontWeights.regular};
  row-gap: ${spacing.base};
  text-align: center;
  white-space: pre-wrap;
`;

const FillPageWrapper = styled.div`
  display: flex;
  margin-top: ${spacing.base};
`;

export const testIds = {
  empty: 'cl-asset-list-empty',
  emptyState: 'cl-asset-list-empty-state',
  emptyStateWrapper: 'cl-asset-list-empty-state-wrapper',
  noResults: 'cl-asset-list-empty-no-results',
  spinner: 'cl-asset-list-empty-spinner',
};

export const AssetListEmptyState = ({
  isDragging = false,
  isLoading = false,
  isPanel = false,
}: {
  isDragging?: boolean;
  isLoading?: boolean;
  isPanel?: boolean;
}) => {
  const { t } = useTranslations();
  const imgInput = useRef<HTMLInputElement>(null);
  const { panelSearch, panelView, hideGifs } = usePanelContext();
  const { searchQuery: indexSearch } = useFilterContext();
  const searchQuery = isPanel ? panelSearch : indexSearch;
  // The empty state will be clickable when there are no assets to show, but not when a filter/search returns no results
  const hasFilter = !!searchQuery;
  const isClickable = isPanel
    ? !(panelView === PanelView.List && hasFilter) && !isLoading
    : !isLoading;

  const handleEmptyStateClick = () => {
    if (isClickable) {
      imgInput.current?.click();
    }
  };

  const emptyStateCTA = () => {
    return isClickable ? (
      <>
        <StyledLabel data-test={testIds.empty}>
          <EmptyStateImages />
          <Trans
            t={t}
            i18nKey="input.url.cta"
            components={{
              ctaSection: <section />,
              ctaButton: <Button size={BUTTON_SIZES.MEDIUM} variant={BUTTON_VARIANTS.SECONDARY} />,
              ctaButtonLabel: <span />,
              ctaCopyIcon: <MakeCopy height="24" width="24" />,
            }}
          >
            <section>Drop images here to upload or</section>
            <Button>
              <MakeCopy height="24" width="24" />
              <span>Browse files</span>
            </Button>
          </Trans>
        </StyledLabel>
        <CaptionSecondary>
          {hideGifs ? t('caption.fileTypesNoGif') : t('caption.fileTypes')}
        </CaptionSecondary>
      </>
    ) : (
      <StyledLabel data-test={testIds.noResults}>
        <NoResult />
        <CtaHeader>{t('header.noMatchingResults')}</CtaHeader>
        <Cta>{t('caption.tryDifferentKeywords')}</Cta>
      </StyledLabel>
    );
  };

  return (
    <EmptyState
      data-test={testIds.emptyState}
      onClick={handleEmptyStateClick}
      $isDragging={isDragging}
      $isClickable={isClickable}
      $isLoading={!!isLoading}
    >
      <HiddenFileInput ref={imgInput} />
      {isLoading ? <Spinner data-test={testIds.spinner} /> : emptyStateCTA()}
    </EmptyState>
  );
};

export const EmptyStateWrapper: FC<React.PropsWithChildren> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [posFromTop, setPosFromTop] = useState<number>(0);

  useEffect(() => {
    setPosFromTop(wrapperRef.current?.getBoundingClientRect().top || 0);
  }, []);

  return (
    <FillPageWrapper
      data-test={testIds.emptyStateWrapper}
      ref={wrapperRef}
      style={{ height: `calc(100vh - ${posFromTop}px - ${spacing.layoutLRPadding})` }}
    >
      {children}
    </FillPageWrapper>
  );
};
